import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, Phone } from "lucide-react";
import { useLang, UI } from "@/i18n";
import { useContent } from "@/content";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { waHref } from "@/components/Nav";
import SocialLinks from "@/components/Social";

const SERVICES = ["airport", "private", "business", "hourly", "scenic", "night"];

export default function ContactSection() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const c = content && content.contact;
  const contacts = (content && content.contacts) || { email: "info@obsidianprivatedriver.it", whatsapp: "393751163111" };
  const F = UI.form;

  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", date: "", time: "",
    departure: "", destination: "", trip_type: "", passengers: "",
    luggage: "", flight_train: "", children: "", pets: "", equipment: "", message: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name || !form.email || !form.phone || !form.service || !form.date || !form.time || !form.departure || !form.destination || !form.trip_type || !form.passengers) {
      setFormError(t(F.required));
      return;
    }
    if (!privacy) {
      setFormError(t(F.privacyRequired));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, privacy_accepted: privacy }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "error");
      setResult(data);
    } catch (err) {
      setFormError("Errore di invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (!c) return null;

  return (
    <section id="contatti" className="relative border-t hairline py-20 sm:py-24 lg:py-28" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-10 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>{t(c.kicker)}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display mt-6 text-3xl font-light leading-tight text-[#FAF9F6] sm:text-4xl lg:text-5xl" data-testid="contact-title">
              {t(c.title)}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-8 text-sm font-light leading-relaxed text-[#A1A1AA] sm:text-base">{t(c.text)}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 border hairline bg-[#101014] p-8" data-testid="contact-direct-box">
              <h3 className="font-display text-2xl font-light text-[#FAF9F6]">{t(c.direct_title)}</h3>
              <p className="mt-3 text-[13px] font-light leading-relaxed text-[#A1A1AA]">{t(c.direct_text)}</p>
              <div className="mt-8 flex flex-col gap-4">
                <a
                  href={waHref(contacts, lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold-bright !px-8"
                  data-testid="contact-whatsapp-cta"
                >
                  <MessageCircle size={15} strokeWidth={1.5} /> WhatsApp · +39 375 116 3111 · Nicky
                </a>
                <a href={`mailto:${contacts.email}`} className="flex items-center gap-3 text-[13px] font-light text-[#FAF9F6]/75 transition-colors hover:text-[#C5A059]" data-testid="contact-email-link">
                  <Mail size={15} strokeWidth={1.25} className="text-[#C5A059]" /> {contacts.email}
                </a>
                <a href="tel:+393751163111" className="flex items-center gap-3 text-[13px] font-light text-[#FAF9F6]/75 transition-colors hover:text-[#C5A059]" data-testid="contact-phone-link">
                  <Phone size={15} strokeWidth={1.25} className="text-[#C5A059]" /> +39 375 116 3111 · Nicky
                </a>
                <SocialLinks className="mt-2" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          {result ? (
            <Reveal>
              <div className="flex h-full flex-col items-start justify-center border hairline bg-[#101014] p-10 sm:p-14" data-testid="contact-success">
                <p className="eyebrow">{t(F.sent)} · {result.ref}</p>
                <p className="font-display mt-6 text-2xl font-light leading-snug text-[#FAF9F6] sm:text-3xl">{t(c.success_msg)}</p>
                <a href={waHref(contacts, lang)} target="_blank" rel="noopener noreferrer" className="btn-outline mt-10" data-testid="contact-success-whatsapp">
                  {t(UI.whatsapp)}
                </a>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} noValidate className="border hairline bg-[#101014] p-8 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label className="label-lux" htmlFor="f-name">{t(F.name)}</label>
                    <input id="f-name" className="input-lux" placeholder={t(F.namePh)} value={form.name} onChange={set("name")} data-testid="form-name" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="label-lux" htmlFor="f-email">{t(F.email)}</label>
                    <input id="f-email" type="email" className="input-lux" placeholder={t(F.emailPh)} value={form.email} onChange={set("email")} data-testid="form-email" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-phone">{t(F.phone)}</label>
                    <input id="f-phone" type="tel" className="input-lux" placeholder={t(F.phonePh)} value={form.phone} onChange={set("phone")} data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-service">{t(F.service)}</label>
                    <select id="f-service" className="input-lux" value={form.service} onChange={set("service")} data-testid="form-service">
                      <option value="">{t(F.servicePh)}</option>
                      {SERVICES.map((id) => (
                        <option key={id} value={id}>{t(UI.services_types[id])}</option>
                      ))}
                      <option value="unknown">{t(UI.services_types.unknown)}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-date">{t(F.date)}</label>
                    <input id="f-date" type="date" className="input-lux [color-scheme:dark]" value={form.date} onChange={set("date")} data-testid="form-date" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-time">{t(F.time)}</label>
                    <input id="f-time" type="time" className="input-lux [color-scheme:dark]" value={form.time} onChange={set("time")} data-testid="form-time" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-dep">{t(F.departure)}</label>
                    <input id="f-dep" className="input-lux" placeholder={t(F.departurePh)} value={form.departure} onChange={set("departure")} data-testid="form-departure" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-dest">{t(F.destination)}</label>
                    <input id="f-dest" className="input-lux" placeholder={t(F.destinationPh)} value={form.destination} onChange={set("destination")} data-testid="form-destination" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-trip">{t(F.trip)}</label>
                    <select id="f-trip" className="input-lux" value={form.trip_type} onChange={set("trip_type")} data-testid="form-trip-type">
                      <option value="">{t(F.trip)}</option>
                      <option value="solo_andata">{t(F.tripOne)}</option>
                      <option value="andata_ritorno">{t(F.tripReturn)}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-pax">{t(F.passengers)}</label>
                    <input id="f-pax" className="input-lux" placeholder={t(F.passengersPh)} value={form.passengers} onChange={set("passengers")} data-testid="form-passengers" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-luggage">{t(F.luggage)}</label>
                    <input id="f-luggage" className="input-lux" placeholder={t(F.luggagePh)} value={form.luggage} onChange={set("luggage")} data-testid="form-luggage" />
                  </div>
                  <div>
                    <label className="label-lux" htmlFor="f-flight">{t(F.flight)}</label>
                    <input id="f-flight" className="input-lux" placeholder={t(F.flightPh)} value={form.flight_train} onChange={set("flight_train")} data-testid="form-flight" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-lux" htmlFor="f-children">{t(F.children)}</label>
                    <input id="f-children" className="input-lux" placeholder={t(F.childrenPh)} value={form.children} onChange={set("children")} data-testid="form-children" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-lux" htmlFor="f-pets">{t(F.pets)}</label>
                    <input id="f-pets" className="input-lux" placeholder="Tipo e dimensione; trasportino" value={form.pets} onChange={set("pets")} data-testid="form-pets" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-lux" htmlFor="f-equip">{t(F.equipment)}</label>
                    <input id="f-equip" className="input-lux" placeholder={t(F.equipmentPh)} value={form.equipment} onChange={set("equipment")} data-testid="form-equipment" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-lux" htmlFor="f-msg">{t(F.message)}</label>
                    <textarea id="f-msg" rows={4} className="input-lux resize-none" placeholder={t(F.messagePh)} value={form.message} onChange={set("message")} data-testid="form-message" />
                  </div>
                </div>

                <label className="mt-8 flex cursor-pointer items-start gap-3 text-[12px] font-light leading-relaxed text-[#A1A1AA]" data-testid="form-privacy-label">
                  <input
                    type="checkbox"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-white/30 bg-transparent checked:bg-[#C5A059] checked:border-[#C5A059] transition-colors"
                    data-testid="form-privacy-checkbox"
                  />
                  <span>
                    {t(c.privacy_label)}{" "}
                    <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2" data-testid="form-privacy-link">
                      Informativa Privacy
                    </Link>
                  </span>
                </label>

                {formError && (
                  <p className="mt-4 border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="form-error">
                    {formError}
                  </p>
                )}

                <button type="submit" disabled={sending} className="btn-gold mt-8 w-full justify-center disabled:opacity-50" data-testid="form-submit">
                  {sending ? t(F.sending) : t(F.submit)}
                </button>
                <p className="mt-4 text-center text-[11px] font-light text-[#FAF9F6]/40">{t(c ? { it: "La richiesta non costituisce conferma della prenotazione. Riceverai disponibilità e preventivo personalizzato.", en: "The request is not a booking confirmation. You will receive availability and a personalised quote." } : "")}</p>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
